"use client";
import { Callout, Card, Flex, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";
import * as CardList from "./card-list.js";
import { DataIntegrationIcon } from "./data-integration-icon.js";
import { ProviderIcon } from "./provider-icon.js";
import {
  DotsHorizontalIcon,
  ExternalLinkIcon,
  GlobeIcon,
  LockClosedIcon,
} from "@radix-ui/react-icons";
import {
  AlertDialog,
  Button,
  Dialog,
  DropdownMenu,
  IconButton,
  Skeleton,
} from "./elements.js";
import { IconPanel } from "./icon-panel.js";
import { Status } from "./status.js";
import {
  DataIntegration,
  useDeleteDataInstallation,
  useGetDataIntegrationAuthorizeUrlHook,
  getMyDataIntegrationsQueryKey,
  useSettings,
} from "../api/endpoint.js";
import { useQueryClient } from "@tanstack/react-query";
import { GenericError } from "./generic-error.js";
import { getDomProps, WidgetRootDomProps, WidgetRootState } from "./utils.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

interface PipesProps extends WidgetRootDomProps {
  integrations: DataIntegration[];
}

const HANDOFF_INTERVAL_MS = 100;
const HANDOFF_MAX_ATTEMPTS = 100; // 10 seconds worth of attempts

const useOpenDataIntegrationAuthorizeUrl = (integration: DataIntegration) => {
  const getDataIntegrationAuthorizeUrl =
    useGetDataIntegrationAuthorizeUrlHook();
  const settings = useSettings();
  const baseUrl = settings.data?.authkitOrigin ?? "";
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined,
  );
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | undefined>(
    undefined,
  );

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (messageHandlerRef.current) {
        window.removeEventListener("message", messageHandlerRef.current);
      }
    };
  }, []);

  return useCallback(async () => {
    // Clear any existing interval from a previous invocation
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
    // Remove any existing message handler
    if (messageHandlerRef.current) {
      window.removeEventListener("message", messageHandlerRef.current);
      messageHandlerRef.current = undefined;
    }

    // need to do this synchronously in the event handler to avoid popup blocker notifications
    const win = window.open(`${baseUrl}/pipes/redirecting`, "_blank");

    // caller will catch
    const { url, handoffToken, redirectToken } =
      await getDataIntegrationAuthorizeUrl(integration.slug, {
        requireHandoff: true,
      });
    if (win) {
      win.location = url;
      const parsedUrl = new URL(url);
      const payload = { handoffToken, redirectToken };

      const cleanup = () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = undefined;
        }
        if (messageHandlerRef.current) {
          window.removeEventListener("message", messageHandlerRef.current);
          messageHandlerRef.current = undefined;
        }
      };

      messageHandlerRef.current = (event: MessageEvent) => {
        if (
          event.origin === parsedUrl.origin &&
          event.data?.redirectToken === redirectToken
        ) {
          cleanup();
        }
      };
      window.addEventListener("message", messageHandlerRef.current);

      let attempts = 0;
      intervalRef.current = setInterval(() => {
        attempts += 1;

        if (attempts >= HANDOFF_MAX_ATTEMPTS) {
          cleanup();
          return;
        }

        if (win && !win.closed) {
          win.postMessage(payload, parsedUrl.origin);
        } else {
          cleanup();
        }
      }, HANDOFF_INTERVAL_MS);
    }
  }, [getDataIntegrationAuthorizeUrl, integration.slug, baseUrl]);
};

function ConnectIntegrationButton({
  integration,
}: {
  integration: DataIntegration;
}) {
  const eventHandler = useOpenDataIntegrationAuthorizeUrl(integration);

  return (
    <Button
      variant="secondary"
      onClick={async () => {
        try {
          await eventHandler();
        } catch {
          // pass - error displayed in new tab
        }
      }}
    >
      <Translation
        defaultMessage="Connect"
        id="quvdFp"
        description="Button to connect an integration"
      />
    </Button>
  );
}

function SharedCredentialsConnectIntegrationButton({
  integration,
}: {
  integration: DataIntegration;
}) {
  const settings = useSettings();
  const [open, setOpen] = useState(false);
  const eventHandler = useOpenDataIntegrationAuthorizeUrl(integration);

  const logo = settings.data?.logoDarkPath ?? settings.data?.logoLightPath;
  const appName = settings.data?.teamName ?? "This application";

  return (
    <>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Content maxWidth="430px">
          <Flex justify="center" align="center" gap="2" mt="2">
            {logo && (
              <>
                <IconPanel
                  color="panel"
                  style={{ width: "48px", height: "48px" }}
                >
                  <ProviderIcon
                    size="2"
                    provider="workos"
                    style={{ backgroundImage: `url(${logo})` }}
                  />
                </IconPanel>
                <DotsHorizontalIcon />
              </>
            )}
            <IconPanel
              color="panel"
              style={{
                ...(logo ? { borderWidth: 0 } : undefined),
                width: "48px",
                height: "48px",
              }}
            >
              <ProviderIcon size="2" provider="workos" />
            </IconPanel>
            <DotsHorizontalIcon />
            <IconPanel color="panel" style={{ width: "48px", height: "48px" }}>
              <DataIntegrationIcon size="2" integration={integration} />
            </IconPanel>
          </Flex>
          <Dialog.Title size="2" mt="5" mb="5" weight={"bold"} align="center">
            <Translation
              defaultMessage="{appName} uses WorkOS to connect to {integrationName}."
              id="ujRDBH"
              description="Dialog title explaining the app connection flow"
              values={{ appName, integrationName: integration.name }}
            />
          </Dialog.Title>
          <Card>
            <Flex direction="column" gap="4">
              <Flex gap="2" align="center">
                <IconPanel
                  color="gray"
                  style={{ width: "32px", height: "32px" }}
                >
                  <GlobeIcon />
                </IconPanel>
                <Flex direction="column">
                  <Text size="1" weight="bold">
                    <Translation
                      defaultMessage="You'll be redirected to sign in with {integrationName}."
                      id="g+n4aY"
                      description="Instruction about redirect to sign in"
                      values={{ integrationName: integration.name }}
                    />
                  </Text>
                  <Text size="1">
                    <Translation
                      defaultMessage="Authorize access to connect your account."
                      id="fxVS34"
                      description="Instruction to authorize access"
                    />
                  </Text>
                </Flex>
              </Flex>
              <Flex gap="2" align="center">
                <IconPanel
                  color="gray"
                  style={{ width: "32px", height: "32px" }}
                >
                  <LockClosedIcon />
                </IconPanel>
                <Flex direction="column">
                  <Text size="1" weight="bold">
                    <Translation
                      defaultMessage="Your credentials remain secure."
                      id="gM3bot"
                      description="Security message about credentials"
                    />
                  </Text>
                  <Text size="1">
                    <Translation
                      defaultMessage="WorkOS never sees your credentials."
                      id="kC9Fwe"
                      description="Security message about WorkOS not seeing credentials"
                    />
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Card>
          <Flex justify="end" gap="3" mt="5">
            <Dialog.Close>
              <Button variant="secondary">
                <Translation
                  defaultMessage="Cancel"
                  id="hHNj31"
                  description="Cancel button text"
                />
              </Button>
            </Dialog.Close>
            <Button
              type="button"
              onClick={async () => {
                try {
                  await eventHandler();
                  setOpen(false);
                } catch {
                  // pass - error displayed in new tab
                }
              }}
            >
              <Translation
                defaultMessage="Connect"
                id="16WmG0"
                description="Connect button with external link"
                trailingSpace
              />
              <ExternalLinkIcon aria-hidden />
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <Translation
          defaultMessage="Connect"
          id="quvdFp"
          description="Button to connect an integration"
        />
      </Button>
    </>
  );
}

function ProviderStatus({ integration }: { integration: DataIntegration }) {
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const authorizeEventHandler = useOpenDataIntegrationAuthorizeUrl(integration);
  const translate = useTranslation();

  const pipeActionsTitle = translate({
    defaultMessage: "Pipe actions",
    id: "1uF9ve",
    description: "Title for pipe actions dropdown button",
  });

  if (integration.installation) {
    return (
      <>
        <DisconnectAccountDialog
          integrationName={integration.name}
          installationId={integration.installation.id}
          open={disconnectOpen}
          onOpenChange={setDisconnectOpen}
        />
        <Flex align="center" gap="4">
          <Status
            state={
              integration.installation.state === "connected"
                ? "success"
                : "error"
            }
          >
            {integration.installation.state === "connected" ? (
              <Translation
                defaultMessage="Connected"
                id="i6Hxoz"
                description="Status text for connected integration"
              />
            ) : (
              <Translation
                defaultMessage="Requires reauthorization"
                id="dqczQS"
                description="Status text for integration needing reauthorization"
              />
            )}
          </Status>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <IconButton title={pipeActionsTitle}>
                <DotsHorizontalIcon />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end">
              {integration.installation.state === "needs_reauthorization" && (
                <DropdownMenu.Item
                  onClick={async () => {
                    try {
                      await authorizeEventHandler();
                    } catch {
                      // pass - error displayed in new tab
                    }
                  }}
                >
                  <Flex gap="4" width="100%" justify="between" align="center">
                    <Translation
                      defaultMessage="Reauthorize"
                      id="0E29F1"
                      description="Menu option to reauthorize an integration"
                      trailingSpace
                    />
                    <ExternalLinkIcon aria-hidden />
                  </Flex>
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item
                variant="destructive"
                onClick={() => setDisconnectOpen(true)}
              >
                <Translation
                  defaultMessage="Disconnect account"
                  id="esZQrd"
                  description="Menu option to disconnect an account"
                />
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </Flex>
      </>
    );
  }

  if (integration.credentialsType === "shared") {
    return (
      <SharedCredentialsConnectIntegrationButton integration={integration} />
    );
  }

  return <ConnectIntegrationButton integration={integration} />;
}

function DisconnectAccountDialog({
  integrationName,
  installationId,
  open,
  onOpenChange,
}: {
  integrationName: string;
  installationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const {
    mutate: deleteDataInstallation,
    error,
    reset,
    isPending,
  } = useDeleteDataInstallation({
    mutation: {
      onSuccess: () => {
        onOpenChange(false);
        queryClient.invalidateQueries({
          queryKey: getMyDataIntegrationsQueryKey(),
        });
      },
    },
  });

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Content style={{ width: "80vw", maxWidth: "520px" }}>
        <AlertDialog.Title>
          <Translation
            defaultMessage="Disconnect account"
            id="9ABlCr"
            description="Dialog title for disconnecting an account"
          />
        </AlertDialog.Title>
        {error && (
          <Callout.Root my="5" color="red" role="alert">
            <Callout.Text>
              <Translation
                defaultMessage="An error occurred while disconnecting your account. Please refresh the page and try again."
                id="qKBKF7"
                description="Error message when disconnecting an account fails"
              />
            </Callout.Text>
          </Callout.Root>
        )}
        <AlertDialog.Description>
          <Translation
            defaultMessage="Are you sure you want to disconnect your {integrationName} account?"
            id="eEXeqP"
            description="Confirmation message for disconnecting an account"
            values={{
              integrationName: (
                <Text as="span" weight="bold">
                  {integrationName}
                </Text>
              ),
            }}
          />
        </AlertDialog.Description>
        <Flex gap="3" justify="end" mt="5">
          <AlertDialog.Cancel>
            <Button variant="secondary">
              <Translation
                defaultMessage="Cancel"
                id="hHNj31"
                description="Cancel button text"
              />
            </Button>
          </AlertDialog.Cancel>
          <Button
            variant="destructive"
            disabled={isPending}
            loading={isPending}
            onClick={() => deleteDataInstallation({ installationId })}
          >
            <Translation
              defaultMessage="Disconnect"
              id="vMVXQh"
              description="Button to disconnect an account"
            />
          </Button>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
}

interface PipesLoadingProps extends WidgetRootDomProps {
  count: number;
}

const PipesLoading: React.FC<PipesLoadingProps> = ({ count, ...domProps }) => {
  return (
    <CardList.Root {...getWidgetRootDomProps("loading", domProps)}>
      {Array.from({ length: count }).map((_, index) => (
        <CardList.Item key={index}>
          <Flex direction="row" justify="between" align="center" gap="2">
            <Flex gap="4" align="center">
              <Skeleton>
                <IconPanel>
                  <ProviderIcon provider="google" />
                </IconPanel>
              </Skeleton>
              <Skeleton>
                <Text size="2" weight="bold">
                  <Translation
                    defaultMessage="Google Drive"
                    id="g11OOu"
                    description="Placeholder text for loading state"
                  />
                </Text>
              </Skeleton>
            </Flex>
            <Skeleton>
              <Button variant="secondary" disabled>
                <Translation
                  defaultMessage="Connect"
                  id="8TuDfA"
                  description="Placeholder button text for loading state"
                />
              </Button>
            </Skeleton>
          </Flex>
        </CardList.Item>
      ))}
    </CardList.Root>
  );
};

const Pipes: React.FC<PipesProps> = ({ integrations, ...domProps }) => {
  return (
    <CardList.Root {...getWidgetRootDomProps("resolved", domProps)}>
      {integrations.map((integration) => (
        <CardList.Item key={integration.id}>
          <Flex direction="row" justify="between" align="center" gap="2">
            <Flex gap="4" align="center">
              <IconPanel color="panel">
                <DataIntegrationIcon integration={integration} />
              </IconPanel>
              <Text size="2" weight="bold">
                {integration.name}
              </Text>
            </Flex>
            <ProviderStatus integration={integration} />
          </Flex>
        </CardList.Item>
      ))}
    </CardList.Root>
  );
};

interface PipesErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const PipesError: React.FC<PipesErrorProps> = ({ error, ...domProps }) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("error", domProps)}>
      <GenericError error={error} />
    </Card>
  );
};

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "pipes",
    widgetState: state,
  });
}

export type { PipesProps, PipesLoadingProps, PipesErrorProps };
export { Pipes, PipesLoading, PipesError };
